import {
  PermissionCode,
  type OnboardingCase,
  type OnboardingCaseKind,
  type OnboardingCaseStatus,
} from '@gb-ticket/shared-types';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ListPagination } from '@/components/list-pagination';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { OnboardingCaseStartDialog } from '@/products/onboarding/features/onboarding-case-start-dialog';
import {
  onboardingPanelClassName,
  onboardingTableFrameClassName,
} from '@/products/onboarding/features/onboarding-panel';
import {
  ONBOARDING_CASE_KIND_LABEL,
  ONBOARDING_CASE_STATUS_LABEL,
  onboardingCaseStateLabel,
  onboardingCaseSubjectName,
  onboardingNamedOrDash,
} from '@/products/onboarding/features/onboarding-template-labels';
import { useOnboardingCases } from '@/products/onboarding/features/use-onboarding-cases';
import { onboardingCasePath } from '@/products/onboarding/onboarding-paths';

const EMPTY_FILTERS = 'Keine Vorgänge zu diesen Filtern.';
const CASE_STATUSES = Object.keys(ONBOARDING_CASE_STATUS_LABEL) as OnboardingCaseStatus[];

export function OnboardingCasesPage() {
  usePageTitle('Vorgänge');
  const copy = useVoiceCopy();
  const me = useMe();
  const navigate = useNavigate();
  const canReadCases =
    hasPermission(me.data, PermissionCode.ONBOARDING_CASES_READ) ||
    hasPermission(me.data, PermissionCode.ONBOARDING_CASES_MANAGE);
  const canManage = hasPermission(me.data, PermissionCode.ONBOARDING_CASES_MANAGE);
  const [startOpen, setStartOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<OnboardingCaseKind | undefined>();
  const [status, setStatus] = useState<OnboardingCaseStatus | undefined>();
  const trimmedQuery = q.trim();
  const hasFilters = trimmedQuery.length > 0 || kind != null || status != null;
  const cases = useOnboardingCases({
    page,
    pageSize: 25,
    sortBy: 'updatedAt',
    sortOrder: 'desc',
    q: trimmedQuery || undefined,
    kind,
    status,
  });

  if (!canReadCases) {
    return <p className="text-sm text-muted-foreground">{copy('onboarding.access.denied')}</p>;
  }

  if (cases.isError && isForbidden(cases.error)) {
    return <p className="text-sm text-muted-foreground">{copy('onboarding.access.denied')}</p>;
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Vorgänge"
        description="Laufende Eintritte und Austritte aus Vorlagen."
        action={
          canManage ? (
            <Button type="button" onClick={() => setStartOpen(true)}>
              Vorgang starten
            </Button>
          ) : null
        }
      />

      <div className={`${onboardingPanelClassName} flex flex-wrap items-end gap-3`}>
        <div className="min-w-[12rem] flex-1 space-y-2">
          <Label htmlFor="onboarding-cases-q">Suche</Label>
          <Input
            id="onboarding-cases-q"
            value={q}
            placeholder="Nummer oder Name"
            onChange={(event) => {
              setQ(event.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="w-44 space-y-2">
          <Label htmlFor="onboarding-cases-kind">Art</Label>
          <Select
            value={kind ?? 'all'}
            onValueChange={(value) => {
              setKind(value === 'all' ? undefined : (value as OnboardingCaseKind));
              setPage(1);
            }}
          >
            <SelectTrigger id="onboarding-cases-kind" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Arten</SelectItem>
              <SelectItem value="onboarding">{ONBOARDING_CASE_KIND_LABEL.onboarding}</SelectItem>
              <SelectItem value="offboarding">{ONBOARDING_CASE_KIND_LABEL.offboarding}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-44 space-y-2">
          <Label htmlFor="onboarding-cases-status">Status</Label>
          <Select
            value={status ?? 'all'}
            onValueChange={(value) => {
              setStatus(value === 'all' ? undefined : (value as OnboardingCaseStatus));
              setPage(1);
            }}
          >
            <SelectTrigger id="onboarding-cases-status" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              {CASE_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {ONBOARDING_CASE_STATUS_LABEL[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {cases.isPending ? (
        <p className="text-sm text-muted-foreground">Vorgänge werden geladen …</p>
      ) : null}

      {cases.isError ? (
        <p className="text-sm text-destructive">
          {cases.error instanceof ApiRequestError
            ? cases.error.message
            : 'Die Vorgänge konnten nicht geladen werden.'}
        </p>
      ) : null}

      {cases.data?.items.length === 0 ? (
        <p className={`${onboardingPanelClassName} text-sm text-muted-foreground`}>
          {hasFilters ? EMPTY_FILTERS : copy('onboarding.cases.empty')}
        </p>
      ) : null}

      {cases.data && cases.data.items.length > 0 ? (
        <div className="space-y-3">
          <ul className={`${onboardingTableFrameClassName} divide-y`}>
            {cases.data.items.map((item) => (
              <li
                key={item.id}
                className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3"
              >
                <div>
                  <Link to={onboardingCasePath(item.id)} className="font-medium hover:underline">
                    {item.number}
                  </Link>
                  <p className="text-sm text-muted-foreground">{caseMeta(item)}</p>
                </div>
              </li>
            ))}
          </ul>
          <ListPagination
            page={cases.data.page}
            pageSize={cases.data.pageSize}
            total={cases.data.total}
            onPageChange={setPage}
          />
        </div>
      ) : null}

      {canManage ? (
        <OnboardingCaseStartDialog
          open={startOpen}
          onOpenChange={setStartOpen}
          onStarted={(id) => {
            setStartOpen(false);
            void navigate(onboardingCasePath(id));
          }}
        />
      ) : null}
    </div>
  );
}

function caseMeta(item: OnboardingCase): string {
  return [
    onboardingCaseSubjectName(item),
    onboardingCaseStateLabel(item),
    formatDate(item.entryOn ?? item.exitOn),
    onboardingNamedOrDash(item.ownerDisplayName),
  ].join(' · ');
}

function isForbidden(error: unknown): boolean {
  return error instanceof ApiRequestError && error.error.statusCode === 403;
}
